/**
 * One area's "waiting on us" count for its sidebar badge. Every badge subscribes to the same
 * `navCountsQuery` and selects its own key, so the whole sidebar is one request. A failed read, a
 * failed counter (`null`) or a missing key is "no badge", never a thrown nav item.
 */
import { useQuery } from "@tanstack/react-query";

import { navCountsQuery } from "@/lib/queries-nav-counts";
import type {
  NavCountKey,
  NavCountPart,
  NavCountsResponse,
} from "@/types-nav-counts";

export interface NavCountView {
  count: number;
  /** The non-empty parts, each a number the tooltip can name. */
  parts: Array<Required<Pick<NavCountPart, "label" | "count">>>;
}

const EMPTY: NavCountView = { count: 0, parts: [] };

function wholeCount(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

export function navCountFrom(
  data: NavCountsResponse | undefined,
  key: NavCountKey,
): NavCountView {
  const entry = data?.counts?.[key];
  if (!entry) return EMPTY;
  const count = wholeCount(entry.count);
  if (count === 0) return EMPTY;
  const parts = (entry.parts ?? [])
    .map((part) => ({
      label: typeof part.label === "string" ? part.label : "",
      count: wholeCount(part.count),
    }))
    .filter((part) => part.count > 0 && part.label !== "");
  return { count, parts };
}

/**
 * The badge's accessible name and hover text: "Partner applications to review: 3; Held partner
 * fees: 1". Label first, number after, so a count of one never reads as a wrong plural.
 */
export function navCountDescription(view: NavCountView): string {
  if (view.parts.length === 0) return `Waiting on us: ${view.count}`;
  return view.parts
    .map(
      (part) =>
        `${part.label.charAt(0).toUpperCase()}${part.label.slice(1)}: ${part.count}`,
    )
    .join("; ");
}

export function useNavCount(key: NavCountKey): NavCountView {
  const { data } = useQuery({
    ...navCountsQuery(),
    select: (response: NavCountsResponse) => navCountFrom(response, key),
  });
  return data ?? EMPTY;
}
